import { clampRect } from '../selection';
import { layerAABB } from './bounds';
import { isGroup } from './tree';
import type { GroupLayer, Layer, Rect, Size } from './types';

/**
 * The composite structure of the layer tree (Iteration 14).
 *
 * Two relationships turn a folder into a compositor citizen, and both resolve
 * here, in pure code, so the engine, the rasteriser and the plans cannot
 * disagree about what the user will see:
 *
 *  - **The group buffer.** A group with an opacity below 1 or a blend mode
 *    other than `normal` composites its children among themselves first and
 *    places the result as one unit (§3 of the design doc). The multiplication
 *    approximation this replaces is wrong exactly when two children overlap.
 *  - **The clipping run.** A layer with `clipping: true` shows only where the
 *    nearest non-clipped sibling *below* it — its base — has alpha (§4). A run
 *    of consecutive clipped layers shares one base; a group may sit on either
 *    side of the stencil.
 *
 * Everything else in the tree keeps the Iteration 11 contract: a neutral group
 * is a folder, and grouping stays pixel-neutral.
 */

/** True when a group composites its children into one buffer. */
export function groupComposites(layer: Layer): boolean {
  return isGroup(layer) && (layer.opacity < 1 || layer.blendMode !== 'normal');
}

/**
 * The parts of the tree, in z-order, with the composite relationships resolved.
 *
 * One skeleton per drawn unit:
 *  - `leaf` — one leaf layer, drawn as itself.
 *  - `clip` — a base and the run of clipped layers above it, drawn as one
 *    stencilled sheet; `base` is always the first member in z-order.
 *  - `buffer` — a non-neutral group; the consumer draws its children into one
 *    canvas and places it with the group's opacity and blend mode.
 *  - `folder` — a neutral group; the consumer walks its children with this
 *    same function.
 *
 * Hidden layers are included: visibility is the consumer's concern (the engine
 * skips them; a hidden base still stencils its clipped layers to nothing).
 * A clipped layer with no base below it is *not* a run — it falls through as a
 * plain leaf, which is the rendering rule for a flag with nothing to bite on.
 */
export type SceneSkeleton =
  | { kind: 'leaf'; layer: Layer }
  | { kind: 'clip'; base: Layer; clipped: Layer[]; bounds: Rect }
  | { kind: 'buffer'; group: GroupLayer; bounds: Rect }
  | { kind: 'folder'; group: GroupLayer };

/** The drawn box of one clip-run member: a leaf's AABB, or a group's composite. */
function clipMemberBounds(layer: Layer, doc: Size): Rect | null {
  if (isGroup(layer)) return compositeBounds(layer.children, doc);
  return layerAABB(layer, doc);
}

/** Union of rects, or null when there is nothing to union. */
function unionRects(rects: Rect[], doc: Size): Rect | null {
  if (rects.length === 0) return null;
  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return clampRect(
    { x: left, y: top, width: right - left, height: bottom - top },
    { x: 0, y: 0, width: doc.width, height: doc.height },
  );
}

export function sceneSkeleton(siblings: Layer[], doc: Size): SceneSkeleton[] {
  const runs = clippingRuns(siblings);
  const clippedIds = new Set(runs.flatMap((run) => run.clipped.map((layer) => layer.id)));
  const runByBase = new Map(runs.map((run) => [run.base.id, run]));

  const units: SceneSkeleton[] = [];
  for (const layer of siblings) {
    // Clipped members are emitted with their base, in its z-order slot.
    if (clippedIds.has(layer.id)) continue;

    const run = runByBase.get(layer.id);
    if (run) {
      const boxes = [run.base, ...run.clipped]
        .map((member) => clipMemberBounds(member, doc))
        .filter((rect): rect is Rect => rect !== null);
      const bounds = unionRects(boxes, doc);
      if (bounds) units.push({ kind: 'clip', base: run.base, clipped: run.clipped, bounds });
      continue;
    }

    if (isGroup(layer)) {
      if (groupComposites(layer)) {
        const bounds = compositeBounds(layer.children, doc);
        if (bounds) units.push({ kind: 'buffer', group: layer, bounds });
      } else {
        units.push({ kind: 'folder', group: layer });
      }
      continue;
    }
    units.push({ kind: 'leaf', layer });
  }
  return units;
}

/**
 * Resolve the clipping runs of one sibling list: base id → the clipped layers
 * that share it, z-order preserved.
 *
 * The skeleton walker uses this to emit `clip` units at the base; keeping it
 * separate keeps both halves individually testable.
 */
export function clippingRuns(siblings: Layer[]): { base: Layer; clipped: Layer[] }[] {
  const runs: { base: Layer; clipped: Layer[] }[] = [];
  let base: Layer | null = null;
  let current: { base: Layer; clipped: Layer[] } | null = null;
  for (const layer of siblings) {
    if (!layer.clipping) {
      base = layer;
      current = null;
      continue;
    }
    if (!base) continue;
    if (!current) {
      current = { base, clipped: [layer] };
      runs.push(current);
    } else {
      current.clipped.push(layer);
    }
  }
  return runs;
}

/**
 * The area a list of layers covers, as drawn — the union of the members'
 * boxes, groups recursed, clamped to the document.
 *
 * Hidden layers contribute nothing: a buffer does not need canvas space for
 * pixels that will not be drawn into it.
 */
export function compositeBounds(children: Layer[], doc: Size): Rect | null {
  const boxes: Rect[] = [];
  const walk = (layers: Layer[]) => {
    for (const layer of layers) {
      if (!layer.visible) continue;
      if (isGroup(layer)) {
        walk(layer.children);
        continue;
      }
      const box = layerAABB(layer, doc);
      if (box) boxes.push(box);
    }
  };
  walk(children);
  if (boxes.length === 0) return null;
  const left = Math.min(...boxes.map((box) => box.x));
  const top = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const bottom = Math.max(...boxes.map((box) => box.y + box.height));
  return clampRect(
    { x: left, y: top, width: right - left, height: bottom - top },
    { x: 0, y: 0, width: doc.width, height: doc.height },
  );
}

/**
 * Whether enabling the clip on `id` would have anything to bite on: some
 * sibling below it that is not itself clipped. Unchecking always works, so
 * only the enabling side is gated by this — the panel disables the checkbox
 * when it is false, and nothing pretends.
 */
export function clipBaseExists(siblings: Layer[], id: string): boolean {
  const index = siblings.findIndex((layer) => layer.id === id);
  if (index <= 0) return false;
  return siblings.slice(0, index).some((layer) => !layer.clipping);
}
